/**
 * Bundle Builder - Main JavaScript
 * Handles all bundle types: simple, multi-category, tiered, workflow
 */

class BundleBuilder {
    constructor() {
        console.log('Bundle Builder: Initializing...');

        this.selectedItems = [];
        this.container = document.querySelector('.bundle-builder');
        this.productId = this.container?.dataset.productId;
        this.bundleMode = this.container?.dataset.bundleMode;
        this.upsellVariantIds = new Set();

        // Since it may be moved outside the container for Safari layout fixes, look globally
        this.summaryBar = document.querySelector('.bundle-summary-bar');

        console.log('Bundle Mode:', this.bundleMode);
        console.log('Summary Bar found:', !!this.summaryBar);

        if (!this.summaryBar) {
            console.error('Bundle Builder: Summary bar not found in container! Cannot initialize.');
            return;
        }

        // DEFAULT VALUES: Prevent 0 which disables "Add" button
        this.itemsRequired = parseInt(this.summaryBar.dataset.itemsRequired) || 2;
        const rawBundlePrice = this.summaryBar.dataset.bundlePrice || '0';
        this.bundlePrice = parseFloat(rawBundlePrice.replace(/[^0-9.]/g, '')) || 0;
        this.categories = this.loadCategories();
        this.workflowSteps = this.loadWorkflowSteps();

        console.log('Items Required:', this.itemsRequired);
        console.log('Bundle Price:', this.bundlePrice);

        this.productType = this.container?.dataset.productType || 'items';

        // Text customization settings
        this.titlePending = this.container?.dataset.titlePending || '';
        this.titleSuccess = this.container?.dataset.titleSuccess || '';
        this.buttonPending = this.container?.dataset.buttonPending || 'Add [count] more';
        this.buttonSuccess = this.container?.dataset.buttonSuccess || 'ADD TO CART';

        // Reserved timer settings
        this.timerEnabled = this.container?.dataset.timerEnabled === 'true';
        this.timerMinutes = parseInt(this.container?.dataset.timerMinutes) || 10;
        this.timerText = this.container?.dataset.timerText || 'Reserved for';
        this.timerInterval = null;
        this.timerEndTime = null;

        console.log('BundleBuilder: Timer Config:', {
            enabled: this.timerEnabled,
            minutes: this.timerMinutes,
            text: this.timerText
        });

        this.loadProgress();

        // Initialize with defaults
        this.tiers = [];
        this.upchargeBaseAmount = 0;
        this.showUpchargeBadge = true;

        // Load static config from JSON if available (replaces brittle DOM scraping)
        const configEl = document.getElementById('bundle-config-data');
        if (configEl) {
            try {
                const config = JSON.parse(configEl.textContent);
                this.tiers = config.tiers || [];
                // Handle different upcharge formats gracefully
                let uBase = config.upchargeBase || 0;
                this.upchargeBaseAmount = uBase > 1000 ? uBase / 100 : uBase;
                this.showUpchargeBadge = config.showUpchargeBadge !== false;
                this.stockEnabled = config.stockEnabled;
                this.stockThreshold = parseInt(config.stockThreshold) || 10;
                this.stockColor = config.stockColor || '#ff0000';
                this.preorderColor = config.preorderColor || '#ffffff';
                this.stockText = config.stockText || 'Low Stock';
                console.log('BundleBuilder: Loaded JSON Config:', config);
            } catch (e) {
                console.error('BundleBuilder: Failed to parse JSON config', e);
            }
        }

        // Fallback to DOM scraping if tiers/upcharge still missing
        if (!this.tiers || this.tiers.length === 0) {
            this.tiers = this.loadTiersFromDOM();
        }
        if (!this.upchargeBaseAmount) {
            this.upchargeBaseAmount = parseInt(this.container?.dataset.upchargeBase || 0) / 100;
        }

        if (this.bundleMode === 'tiered' && this.tiers.length > 0) {
            this.itemsRequired = Math.min(...this.tiers.map(t => t.items));
            this.bundlePrice = this.tiers.find(t => t.items === this.itemsRequired)?.price || this.bundlePrice;
        }

        // Load Main Bundle Product Data (for syncing variant prices)
        const bundleProductScript = document.getElementById('bundle-product-data');
        if (bundleProductScript) {
            try {
                this.bundleProductData = JSON.parse(bundleProductScript.textContent);
                console.log('BundleBuilder: Loaded Bundle Product Data:', this.bundleProductData.title);
            } catch (e) {
                console.error('BundleBuilder: Error parsing bundle product data', e);
            }
        }

        this.init();
    }

    static cleanId(id) {
        if (!id) return '';
        let sid = String(id).trim();
        if (sid.includes('/')) {
            sid = sid.split('/').pop();
        }
        return sid.replace(/[^0-9]/g, '');
    }

    init() {
        console.log('Initializing bundle builder [v12] (FIXED)...');

        // EVENT DELEGATION: Fixes "dead clicks" on carousel items
        this.container.removeEventListener('click', this.handleContainerClick);
        this.handleContainerClick = this.handleContainerClick.bind(this);
        this.container.addEventListener('click', this.handleContainerClick);

        // Variant Modal (Slide Up) initialization
        this.initVariantModal();

        this.container.addEventListener('change', (e) => {
            if (e.target.closest('.product-options__section[data-js-option-select]')) {
                this.handleSizeChange(e);
            }
        });

        // Summary binding
        const headerBtn = this.summaryBar.querySelector('.bundle-summary__header');
        if (headerBtn) {
            headerBtn.addEventListener('click', () => this.toggleSummary());
        }

        const ctaBtn = this.summaryBar.querySelector('.bundle-summary__cta');
        if (ctaBtn) {
            ctaBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleCheckout();
            });
        }

        this.summaryTitle = this.summaryBar.querySelector('.bundle-summary__title');
        if (this.summaryTitle) {
            this.defaultSummaryTitle = this.summaryTitle.textContent;
        }

        this.updateSummaryBar();
        this.updateProgress();
        this.initStickyReveal(); // Setup scroll-based sticky reveal
        console.log('initStickyReveal call completed');

        // Store original static upsells
        const track = this.summaryBar.querySelector('.upsell-slider-track');
        if (track) {
            this.originalUpsellHTML = track.innerHTML;
        }
        this.currentUpsellSeedId = null;
        this.isFetchingUpsells = false;

        // Upsell buttons binding (Delegation)
        if (this.summaryBar) {
            this.summaryBar.addEventListener('click', (e) => {
                const upsellBtn = e.target.closest('.upsell-item-add-btn');
                if (upsellBtn) {
                    e.preventDefault();
                    this.handleUpsellAdd(upsellBtn);
                }
            });

            // Handle Upsell Variant Change
            this.summaryBar.addEventListener('change', (e) => {
                const variantSelect = e.target.closest('.upsell-variant-select');
                if (variantSelect) {
                    this.handleUpsellVariantChange(variantSelect);
                }
            });
        }

        this.initImageCarousels();
        this.updateSummaryBar();

        // Global Listener for Quick View "Add to Bundle" and click outside
        document.body.addEventListener('click', (e) => {
            const btn = e.target.closest('.js-add-to-bundle-from-quickview');
            if (btn && window.bundleBuilder) {
                e.preventDefault();
                e.stopPropagation();
                const itemData = {
                    productId: btn.dataset.productId,
                    variantId: btn.dataset.variantId,
                    title: btn.dataset.productTitle,
                    image: btn.dataset.productImage,
                    price: btn.dataset.productPrice,
                    comparePrice: btn.dataset.productComparePrice,
                    variantTitle: btn.dataset.variantTitle,
                    category: btn.dataset.category,
                    categoryName: btn.dataset.categoryName,
                    step: btn.dataset.step,
                    stepName: btn.dataset.stepName,
                    stepNumber: btn.dataset.stepNumber
                };
                window.bundleBuilder.addItemFromQuickView(itemData, btn);
            }

            // Close custom swatch dropdowns on click outside
            if (!e.target.closest('.bundle-swatch-dropdown')) {
                document.querySelectorAll('.bundle-swatch-dropdown.active').forEach(dropdown => {
                    dropdown.classList.remove('active');
                });
            }
        });

        // Update all product cards to reflect loaded progress
        if (this.selectedItems.length > 0) {
            console.log('BundleBuilder: Refreshing UI for all product cards...');
            document.querySelectorAll('.bundle-product-item').forEach(card => {
                this.updateCardUI(card);
            });
            // Only start timer if bundle is complete
            if (this.isBundleComplete()) {
                this.startReservedTimer();
            }
        }
    }


    handleContainerClick(e) {
        console.log('BundleBuilder: Container Click Detected on:', e.target);

        // 1. Add/Remove Button
        const addBtn = e.target.closest('.bundle-add-to-bundle-btn');
        if (addBtn) {
            // [FIX] Priority: Check if this is a modal trigger
            if (addBtn.classList.contains('js-open-variant-modal')) {
                console.log('BundleBuilder: IS MODAL TRIGGER. Stopping propagation.');
                e.preventDefault();
                e.stopPropagation();
                // Ensure the function exists before calling, though it should
                if (typeof this.openVariantModal === 'function') {
                    console.log('BundleBuilder: Calling openVariantModal...');
                    this.openVariantModal(addBtn);
                } else {
                    console.error('BundleBuilder: openVariantModal not found!');
                }
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            this.handleAddClick(addBtn);
            return;
        }

        // 2. Color Swatch
        const swatch = e.target.closest('.product-options__value--circle:not(.bundle-swatch-dropdown__selected .product-options__value--circle)');
        if (swatch) {
            this.handleSwatchClick(swatch);
            return;
        }

        // 3. Variant Pill
        const pill = e.target.closest('.variant-pill');
        if (pill) {
            this.handleVariantPillClick(pill);
            return;
        }

        // 4. Summary Remove
        const removeBtn = e.target.closest('.bundle-summary-item__remove');
        if (removeBtn) {
            e.preventDefault();
            const index = parseInt(removeBtn.dataset.index);
            this.removeItem(index);
            return;
        }

        // 5. Quantity Plus
        const plusBtn = e.target.closest('.bundle-qty-plus');
        if (plusBtn) {
            e.preventDefault();
            this.handleQuantityChange(plusBtn, 1);
            return;
        }

        // 6. Quantity Minus
        const minusBtn = e.target.closest('.bundle-qty-minus');
        if (minusBtn) {
            e.preventDefault();
            this.handleQuantityChange(minusBtn, -1);
            return;
        }

        // 7. Custom Swatch Dropdown Toggle
        const swatchDropdown = e.target.closest('.bundle-swatch-dropdown');
        if (swatchDropdown && !e.target.closest('.bundle-swatch-dropdown__list')) {
            e.preventDefault();
            const isActive = swatchDropdown.classList.contains('active');
            // Close other open ones
            document.querySelectorAll('.bundle-swatch-dropdown.active').forEach(d => d.classList.remove('active'));
            if (!isActive) swatchDropdown.classList.add('active');
            return;
        }

        // 8. Custom Swatch Dropdown Item Selection
        const swatchItem = e.target.closest('.bundle-swatch-dropdown__item');
        if (swatchItem) {
            e.preventDefault();
            this.handleSwatchDropdownItemClick(swatchItem);
            return;
        }
    }



    initImageCarousels() {
        const carousels = document.querySelectorAll('.bundle-image-carousel');
        const getMode = () => {
            const toggle = document.querySelector('.model-view-toggle-input');
            return toggle ? toggle.checked : true; // checked = model
        };

        const findSlideIndex = (slides, type) => {
            let idx = Array.from(slides).findIndex(s => s.dataset.imageType === type);
            // Fallback: If looking for standalone but only default exists
            if (idx === -1 && type === 'standalone') idx = Array.from(slides).findIndex(s => s.dataset.imageType === 'default');
            // Final fallback: First slide
            return idx === -1 ? 0 : idx;
        };

        this._lastModelView = getMode();

        carousels.forEach(carousel => {
            const slides = carousel.querySelectorAll('.carousel-slide');
            const dots = carousel.querySelectorAll('.dot');

            if (slides.length === 0) return;

            // Initial Sync
            const initialType = getMode() ? 'model' : 'standalone';
            this.updateCarousel(carousel, findSlideIndex(slides, initialType));

            // 1. Touch events (Mobile)
            let touchStartX = 0;
            carousel.addEventListener('touchstart', e => { touchStartX = e.changedTouches[0].screenX; }, { passive: true });
            carousel.addEventListener('touchend', e => {
                const touchEndX = e.changedTouches[0].screenX;
                this.handleSwipe(carousel, slides, dots, touchStartX, touchEndX);
            }, { passive: true });

            // 2. Hover events (Desktop)
            carousel.addEventListener('mouseenter', () => {
                const hoverType = getMode() ? 'standalone' : 'model';
                const idx = findSlideIndex(slides, hoverType);
                // Only switch if we actually found a DIFFERENT slide
                const currentIdx = Array.from(slides).findIndex(s => s.classList.contains('active'));
                if (idx !== currentIdx) this.updateCarousel(carousel, idx);
            });

            carousel.addEventListener('mouseleave', () => {
                const baseType = getMode() ? 'model' : 'standalone';
                this.updateCarousel(carousel, findSlideIndex(slides, baseType));
            });

            // 3. Dots
            dots.forEach((dot, index) => {
                dot.addEventListener('click', (e) => {
                    e.preventDefault(); e.stopPropagation();
                    this.updateCarousel(carousel, index);
                });
            });
        });

        // 4. Global Sync on Toggle (header-scripts triggers 'resize')
        window.addEventListener('resize', () => {
            const currentMode = getMode();
            if (this._lastModelView !== currentMode) {
                this._lastModelView = currentMode;
                const targetType = currentMode ? 'model' : 'standalone';
                document.querySelectorAll('.bundle-image-carousel').forEach(c => {
                    const s = c.querySelectorAll('.carousel-slide');
                    this.updateCarousel(c, findSlideIndex(s, targetType));
                });
            }
        });
    }

    handleSwipe(carousel, slides, dots, startX, endX) {
        // Find active slide index
        let activeIndex = Array.from(slides).findIndex(s => s.classList.contains('active'));

        if (endX < startX - 50) {
            // Swipe Left (Next)
            activeIndex++;
        } else if (endX > startX + 50) {
            // Swipe Right (Prev)
            activeIndex--;
        }

        // Bounds check
        if (activeIndex < 0) activeIndex = 0;
        if (activeIndex >= slides.length) activeIndex = slides.length - 1;

        this.updateCarousel(carousel, activeIndex);
    }

    updateCarousel(carousel, index) {
        const track = carousel.querySelector('.carousel-track');
        const slides = carousel.querySelectorAll('.carousel-slide');
        const dots = carousel.querySelectorAll('.dot');

        // Update slides
        slides.forEach(s => s.classList.remove('active'));
        if (slides[index]) slides[index].classList.add('active');

        // Update dots
        dots.forEach(d => d.classList.remove('active'));
        if (dots[index]) dots[index].classList.add('active');

        // Scroll track
        if (track && slides[0]) {
            const itemWidth = slides[0].offsetWidth;
            track.scrollTo({
                left: itemWidth * index,
                behavior: 'smooth'
            });
        }
    }

    handleSwatchClick(swatch) {
        console.log('BundleBuilder: [DEBUG] handleSwatchClick triggered', swatch.dataset.value);
        const section = swatch.closest('.product-options__section');
        const card = swatch.closest('.bundle-product-item');

        // Update selected state
        section.querySelectorAll('.product-options__value--circle').forEach(s => {
            s.classList.remove('active');
            const input = s.querySelector('input');
            if (input) input.checked = false;
        });

        swatch.classList.add('active');
        const input = swatch.querySelector('input');
        if (input) {
            input.checked = true;
            // Sync to corresponding hidden select if it exists
            const pos = input.dataset.optionPosition;
            const select = card.querySelector(`select[data-option-index="${parseInt(pos) - 1}"]`);
            if (select) select.value = input.value;
        }

        // Update variant label
        const optionName = swatch.dataset.optionName;
        const optionValue = swatch.dataset.value;
        const label = card.querySelector(`[data-variant-label="${optionName}"]`);
        if (label) {
            label.textContent = optionValue;
        }

        this.syncCardUI(card);
        this.updateVariantId(card);
    }

    handleSizeChange(e) {
        const select = e.target;
        const card = select.closest('.bundle-product-item');
        console.log('BundleBuilder: [v12] handleSizeChange triggered', {
            optionName: select.dataset.optionName,
            selectedValue: select.value
        });

        this.syncCardUI(card);
        this.updateVariantId(card);
        this.updateCardUI(card);
    }

    syncCardUI(card) {
        const productData = this.getProductData(card);
        if (!productData) return;

        // 1. Get all selected options using the robust helper
        const options = this.getSelectedOptions(card);

        // 2. Find matching variant based on ALL option positions
        const variant = productData.variants.find(v => {
            let match = true;
            for (let i = 1; i <= 3; i++) {
                const val = options[i.toString()];
                if (val && v['option' + i] !== val) {
                    match = false;
                    break;
                }
            }
            return match;
        });

        if (variant) {
            // 3. Update Custom Swatch Dropdown visual indicators
            // We still iterate through selects to sync their specific visual dropdown wrappers
            const allSelects = card.querySelectorAll('select.product-options__section[data-js-option-select]');
            allSelects.forEach(s => {
                const wrap = s.closest('.unique-dropdown-wrapper');
                if (!wrap) return;

                const idx = parseInt(s.dataset.optionIndex);
                const pos = s.dataset.optionPosition || (idx + 1);
                const val = options[pos.toString()];

                const swatch = wrap.querySelector('.selected-swatch-indicator');
                const textDisp = wrap.querySelector('.swatch-text-value');
                const optName = s.dataset.optionName || '';
                const isColorOpt = ['color', 'colour', 'material', 'finish', 'metal', 'tint', 'frame', 'stone', 'style'].some(k => optName.toLowerCase().includes(k));

                if (swatch) {
                    // Find image for this specific option value if available
                    const dispVar = productData.variants.find(v => v.options[idx] === val && (v.featured_image || v.image));
                    const img = dispVar ? (dispVar.featured_image || dispVar.image) : null;

                    swatch.style.setProperty('background-image', 'none', 'important');
                    swatch.style.setProperty('background-color', 'transparent', 'important');
                    swatch.className = 'selected-swatch-indicator product-options__value product-options__value--circle rounded-circle active';

                    if (val) {
                        const handle = val.toLowerCase().replace(/\s+/g, '-');
                        if (isColorOpt) {
                            if (handle.includes('gold')) {
                                if (handle.includes('rose')) {
                                    swatch.style.setProperty('background-image', 'linear-gradient(135deg, #e0a899 0%, #fbd8c0 45%, #e0a899 100%)', 'important');
                                } else {
                                    swatch.style.setProperty('background-image', 'linear-gradient(135deg, #d4af37 0%, #f9f295 45%, #e6be8a 70%, #b8860b 100%)', 'important');
                                }
                            } else if (handle.includes('silver')) {
                                swatch.style.setProperty('background-image', 'linear-gradient(135deg, #bdc3c7 0%, #fafafa 45%, #bdc3c7 100%)', 'important');
                            } else if (handle.includes('space-grey') || handle.includes('space-gray') || handle.includes('black')) {
                                swatch.style.setProperty('background-image', 'linear-gradient(135deg, #2c3e50 0%, #1e272e 100%)', 'important');
                            } else if (img && img.src) {
                                const url = img.src.startsWith('//') ? `https:${img.src}` : img.src;
                                swatch.style.setProperty('background-image', `url("${url}")`, 'important');
                                swatch.style.setProperty('background-size', 'cover', 'important');
                            } else {
                                swatch.classList.add(`color-${handle}`);
                            }
                        } else if (img && img.src) {
                            const url = img.src.startsWith('//') ? `https:${img.src}` : img.src;
                            swatch.style.setProperty('background-image', `url("${url}")`, 'important');
                            swatch.style.setProperty('background-size', 'cover', 'important');
                        }
                    }
                }
                if (textDisp) {
                    textDisp.textContent = val;
                    if (isColorOpt) {
                        textDisp.style.setProperty('display', 'none', 'important');
                    } else {
                        textDisp.style.setProperty('display', '', '');
                    }
                }
            });

            // 4. Update Main Product Image / Carousel
            const img = variant.featured_image || variant.image;
            if (img && img.src) {
                const carousel = card.querySelector('.bundle-image-carousel');
                if (carousel) {
                    const slides = carousel.querySelectorAll('.carousel-slide');
                    let targetIndex = -1;
                    slides.forEach((slide, idx) => {
                        if (slide.getAttribute('data-image-id') === String(img.id)) {
                            targetIndex = idx;
                        }
                    });

                    if (targetIndex !== -1) {
                        this.updateCarousel(carousel, targetIndex);
                    } else {
                        this.updateCarousel(carousel, 0);
                        const url = img.src.startsWith('//') ? `https:${img.src}` : img.src;
                        const cardImg = carousel.querySelector('img.default_media');
                        if (cardImg) {
                            cardImg.src = url;
                            cardImg.srcset = url;
                        }
                    }
                } else {
                    const url = img.src.startsWith('//') ? `https:${img.src}` : img.src;
                    const cardImg = card.querySelector('.card__media img') ||
                        card.querySelector('.product-collection__image img');
                    if (cardImg) {
                        cardImg.src = url;
                        cardImg.srcset = url;
                    }
                }
            }
        }
    }

    handleSwatchDropdownItemClick(item) {
        console.log('BundleBuilder: [v12] handleSwatchDropdownItemClick triggered');
        const dropdown = item.closest('.bundle-swatch-dropdown');
        const selected = dropdown.querySelector('.bundle-swatch-dropdown__selected');
        const wrapper = dropdown.closest('.js-select') || dropdown.closest('.unique-dropdown-wrapper');
        const select = wrapper?.querySelector('select');
        const value = item.dataset.value;

        if (!select) {
            console.error('BundleBuilder: No hidden select found for dropdown');
            return;
        }

        // 1. Update Hidden Select
        select.value = value;

        // 2. Trigger change event for existing logic (handleSizeChange)
        console.log('BundleBuilder: Triggering change event for value:', value);
        const event = new Event('change', { bubbles: true });
        select.dispatchEvent(event);

        // 3. Update Custom UI State (Active class)
        dropdown.querySelectorAll('.bundle-swatch-dropdown__item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');

        // 4. Close dropdown
        dropdown.classList.remove('active');
    }

    handleVariantPillClick(pill) {
        console.log('BundleBuilder: [v12] handleVariantPillClick triggered');
        if (pill.classList.contains('disabled')) return;

        const slider = pill.closest('.variant-pill-slider');
        const card = pill.closest('.bundle-product-item');

        // Update selection state
        slider.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('selected'));
        pill.classList.add('selected');

        // Sync to corresponding hidden select if it exists
        const pos = pill.dataset.optionPosition;
        const select = card.querySelector(`select[data-option-index="${parseInt(pos) - 1}"]`);
        if (select) select.value = pill.dataset.value;

        // Update label
        const optionName = slider.dataset.optionName;
        const optionValue = pill.dataset.value;
        const label = card.querySelector(`[data-variant-label="${optionName}"]`);
        if (label) {
            label.textContent = optionValue;
        }

        this.syncCardUI(card);
        this.updateVariantId(card);
    }

    openVariantModal(btn) {
        console.log('BundleBuilder: openVariantModal CALLED', btn);
        const productId = btn.dataset.productId;
        const card = btn.closest('.bundle-product-item');

        if (!card) {
            console.error('BundleBuilder: Could not find card for modal trigger');
            return;
        }

        // Hide Summary Bar
        const summaryBar = document.querySelector('.bundle-summary-bar');
        if (summaryBar) summaryBar.classList.add('hidden-behind-modal');

        console.log('BundleBuilder: Processing card for product', productId);

        this.activeModalCard = card;
        const title = btn.dataset.productTitle;
        const titleEl = this.variantModal.querySelector('.variant-slide-up-title');
        if (titleEl) {
            titleEl.textContent = 'SELECT SIZE';
        } else {
            console.error('BundleBuilder: .variant-slide-up-title NOT FOUND');
        }

        // Populate Options
        try {
            this.renderModalOptions(card);
        } catch (e) {
            console.error('BundleBuilder: CRASH in renderModalOptions', e);
        }

        // Show Modal
        if (this.variantModal) {
            console.log('BundleBuilder: Showing Modal (adding .active class)');
            this.variantModal.classList.add('active');
            document.body.style.overflow = 'hidden'; // Lock scroll
        } else {
            console.error('BundleBuilder: this.variantModal is undefined/null');
        }
    }

    handleAddClick(btn) {
        console.log('BundleBuilder: handleAddClick triggered');
        const card = btn.closest('.bundle-product-item');

        if (!this.canAddItem(card)) {
            console.log('BundleBuilder: canAddItem returned false');
            return;
        }

        const item = this.buildItemData(card, btn);
        this.addItem(item, card);
    }

    handleQuantityChange(btn, delta) {
        const card = btn.closest('.bundle-product-item');
        const addBtn = card.querySelector('.bundle-add-to-bundle-btn');

        if (delta > 0) {
            if (!this.canAddItem(card)) return;
            const item = this.buildItemData(card, addBtn);
            this.addItem(item, card);
        } else {
            const variantId = this.getSelectedVariantId(card);
            // Find last instance of this variant added from THIS card (to keep it simple)
            const index = this.selectedItems.map(it => it.id).lastIndexOf(variantId);
            if (index > -1) {
                this.removeItem(index);
            }
        }
    }

    getSelectedVariantId(card) {
        const btn = card.querySelector('.bundle-add-to-bundle-btn');
        return btn.dataset.variantId || btn.dataset.productId;
    }

    canAddItem(card) {
        if (this.bundleMode === 'simple') {
            return this.selectedItems.length < this.itemsRequired;
        } else if (this.bundleMode === 'multi_category') {
            const categoryId = card.dataset.category;
            const category = this.categories.find(c => c.id === categoryId);
            if (!category) return false;

            const categoryItems = this.selectedItems.filter(item => item.category === categoryId);
            return categoryItems.length < category.itemsRequired;
        } else if (this.bundleMode === 'tiered') {
            // For tiered bundles, allow items up to the HIGHEST tier's requirement
            if (this.tiers && this.tiers.length > 0) {
                const highestTier = this.tiers[this.tiers.length - 1];
                return this.selectedItems.length < highestTier.items;
            }
            // Fallback to a reasonable max if tiers aren't loaded
            return this.selectedItems.length < 12;
        } else if (this.bundleMode === 'workflow') {
            const stepId = card.dataset.step;
            const stepItems = this.selectedItems.filter(item => item.step === stepId);
            return stepItems.length < 1; // One item per step
        }
        return true;
    }

    buildItemData(card, btn) {
        const rawPrice = btn.dataset.productPrice;
        const rawCompare = btn.dataset.productComparePrice;

        console.log('BundleBuilder: buildItemData - Raw Prices:', { rawPrice, rawCompare });

        const item = {
            id: btn.dataset.variantId || btn.dataset.productId,
            productId: card.dataset.productId,
            title: btn.dataset.productTitle,
            image: btn.dataset.productImage,
            price: (parseFloat(rawPrice) || 0) / 100,
            comparePrice: (parseFloat(rawCompare) || 0) / 100,
            variant: this.getSelectedVariant(card),
            cardElement: card,
            isPreorder: btn.dataset.isPreorder === 'true',
            preorderLeadTime: parseInt(btn.dataset.preorderLeadTime) || 30
        };

        console.log('BundleBuilder: buildItemData - Parsed Item:', {
            title: item.title,
            price: item.price,
            id: item.id
        });

        if (this.bundleMode === 'multi_category') {
            item.category = card.dataset.category;
            item.categoryName = btn.dataset.categoryName;
        } else if (this.bundleMode === 'workflow') {
            item.step = card.dataset.step;
            item.stepName = btn.dataset.stepName;
            item.stepNumber = parseInt(card.dataset.stepNumber);
        }

        return item;
    }

    getSelectedVariant(card) {
        const options = this.getSelectedOptions(card);
        // Sort by position keys (1, 2, 3) and return as joined string
        return Object.keys(options)
            .sort()
            .map(key => options[key])
            .filter(Boolean)
            .join(' / ');
    }

    getSelectedOptions(card) {
        const options = {};

        // 1. Color/Swatches (Radios)
        const swatchInputs = card.querySelectorAll('.swatch-input__input:checked');
        swatchInputs.forEach(input => {
            const pos = input.dataset.optionPosition;
            if (pos) options[pos] = input.value;
        });

        // 2. Pills
        const selectedPills = card.querySelectorAll('.variant-pill.selected');
        selectedPills.forEach(pill => {
            const pos = pill.dataset.optionPosition;
            if (pos) options[pos] = pill.dataset.value;
        });

        // 3. Dropdowns (Selects)
        const selects = card.querySelectorAll('select.product-options__section[data-style="select"]');
        selects.forEach(select => {
            // Only use if not hidden (handled by JS toggles for pills)
            if (select.offsetParent !== null) {
                const pos = select.dataset.optionPosition || (parseInt(select.dataset.optionIndex) + 1);
                if (pos) options[pos] = select.value;
            }
        });

        return options;
    }

    getProductData(card) {
        const script = card.querySelector('.data-json-product');
        if (!script) return null;
        try {
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('Error parsing product data', e);
            return null;
        }
    }

    updateVariantId(card) {
        const productData = this.getProductData(card);
        if (!productData) return;

        // Use our new consolidated helper
        const options = this.getSelectedOptions(card);
        console.log('BundleBuilder: Current selected options:', options);

        // Find matching variant
        const variant = productData.variants.find(v => {
            // Check if all selected options match
            // v.option1, v.option2, v.option3
            let match = true;
            for (let i = 1; i <= 3; i++) {
                const key = i.toString();
                if (options[key] && v['option' + i] !== options[key]) {
                    match = false;
                }
            }
            return match;
        });

        const btn = card.querySelector('.bundle-add-to-bundle-btn');
        if (variant && btn) {
            console.log('Found variant:', variant.id, variant.title);
            btn.dataset.variantId = variant.id;
            btn.dataset.productPrice = variant.price;
            if (variant.compare_at_price) {
                btn.dataset.productComparePrice = variant.compare_at_price;
            } else {
                btn.dataset.productComparePrice = 0;
            }

            // Get inventory info for the variant from the productData
            const variantInfo = (productData.variants_info || []).find(vq => vq.id == variant.id) || {};
            const inventoryQuantity = parseInt(variantInfo.quantity) || 0;
            const inventoryPolicy = variantInfo.policy || 'deny';

            // Update Preorder state
            const isPreorder = variant.available && inventoryQuantity <= 0 && inventoryPolicy === 'continue';
            btn.dataset.isPreorder = isPreorder.toString();

            // Sync Variant Image to button for Summary Bar
            if (variant.featured_image) {
                btn.dataset.productImage = variant.featured_image.src;
            } else if (variant.image) {
                btn.dataset.productImage = variant.image;
            }

            // Update Sold Out state
            // CRITICAL: Do NOT change button text if it's a modal trigger (Choose Options button)
            const isModalTrigger = btn.classList.contains('js-open-variant-modal');

            if (!variant.available) {
                btn.disabled = true;
                if (!isModalTrigger) {
                    btn.querySelector('.text-add').textContent = 'Sold Out';
                }
                btn.classList.add('sold-out');
            } else {
                btn.disabled = false;
                if (!isModalTrigger) {
                    btn.querySelector('.text-add').textContent = isPreorder ? 'PREORDER' : 'ADD';
                }
                btn.classList.remove('sold-out');
            }

            // Update Badge (Pulsing dot)
            const stockBadge = card.querySelector('[data-js-stock-badge]');
            if (stockBadge) {
                const dot = stockBadge.querySelector('.pulsing-dot');
                const text = stockBadge.querySelector('.badge-text');

                if (isPreorder) {
                    stockBadge.style.display = 'flex';
                    stockBadge.classList.add('preorder-badge');
                    if (dot) {
                        dot.style.display = 'none';
                    }
                    if (text) {
                        // Calculate preorder shipping date (default 30 days)
                        const leadTime = parseInt(btn.dataset.preorderLeadTime) || 30;
                        const shipDate = new Date();
                        shipDate.setDate(shipDate.getDate() + leadTime);
                        const dateStr = shipDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

                        text.textContent = `SHIPS BY ${dateStr}`;
                        text.style.fontWeight = '700';
                    }
                } else if (this.stockEnabled && variant.inventory_management === 'shopify' && inventoryQuantity > 0 && inventoryQuantity <= this.stockThreshold) {
                    stockBadge.style.display = 'flex';
                    stockBadge.classList.remove('preorder-badge');
                    if (dot) {
                        dot.style.backgroundColor = this.stockColor;
                        dot.style.setProperty('--stock-pulse-color', this.stockColor);
                    }
                    if (text) {
                        const lowStockText = inventoryQuantity <= 9 ? `Only ${inventoryQuantity} Left` : (this.stockText || 'Low Stock');
                        text.textContent = lowStockText;
                        text.style.fontWeight = '';
                    }
                } else {
                    stockBadge.style.setProperty('display', 'none', 'important');
                }
            }

            // [v12] Dynamic Upcharge Badge Update
            // Logic: If badging is enabled AND base amount > 0 AND variant price > base amount
            const upchargeBaseCents = (this.upchargeBaseAmount || 0) * 100;
            const cardBadgesContainer = card.querySelector('.bundle-card-badges');

            if (this.showUpchargeBadge && upchargeBaseCents > 0 && cardBadgesContainer) {
                let upchargeBadge = cardBadgesContainer.querySelector('.bundle-product-badge');

                if (variant.price > upchargeBaseCents) {
                    const upchargeAmount = (variant.price - upchargeBaseCents) / 100;
                    const upchargeText = `+ $${upchargeAmount.toFixed(0)}`; // Assuming whole dollars as per liquid

                    if (!upchargeBadge) {
                        upchargeBadge = document.createElement('div');
                        upchargeBadge.className = 'bundle-product-badge';
                        cardBadgesContainer.appendChild(upchargeBadge);
                    }

                    upchargeBadge.textContent = upchargeText;
                    upchargeBadge.style.display = 'block';
                    console.log(`BundleBuilder: Showing upcharge badge for ${variant.title}: ${upchargeText}`);
                } else {
                    // Hide if price is <= base
                    if (upchargeBadge) {
                        upchargeBadge.style.display = 'none';
                    }
                }
            } else if (cardBadgesContainer) {
                // Safety: Ensure hidden if settings disabled
                const existingBadge = cardBadgesContainer.querySelector('.bundle-product-badge');
                if (existingBadge) existingBadge.style.display = 'none';
            }
        } else {
            console.log('No matching variant found');
        }
    }

    addItem(item, card) {
        this.selectedItems.push(item);

        this.updateSummaryBar();
        this.updateProgress();
        this.updateCategoryTabs();
        this.updateTierProgress();
        this.updateWorkflowButtons();
        this.updateCardUI(card);

        this.saveProgress();

        if (this.isBundleComplete()) {
            this.enableCheckout();
            const header = this.summaryBar.querySelector('.bundle-summary__header');
            if (header && header.getAttribute('aria-expanded') !== 'true') {
                this.toggleSummary();
            }
        }

        console.log('BundleBuilder: addItem complete. Calling fetchDynamicUpsells...');
        // Dynamic Upsells: Fetch related to the selection
        this.fetchDynamicUpsells();
        console.log('BundleBuilder: addItem: fetchDynamicUpsells() synchronous call finished');
    }

    removeItem(index) {
        const item = this.selectedItems[index];
        const card = item ? item.cardElement : null;

        this.selectedItems.splice(index, 1);
        this.updateSummaryBar();
        this.updateProgress();
        this.updateCategoryTabs();
        this.updateTierProgress();
        this.updateWorkflowButtons();

        this.saveProgress();

        if (card) {
            this.updateCardUI(card);
        }

        if (!this.isBundleComplete()) {
            this.disableCheckout();
        }

        // Dynamic Upsells: Selection changed
        this.fetchDynamicUpsells();
    }

    updateCardUI(card) {
        if (!card) return;
        const variantId = this.getSelectedVariantId(card);
        const count = this.selectedItems.filter(item => item.id === variantId).length;

        const addBtn = card.querySelector('.bundle-add-to-bundle-btn');
        const qtySelector = card.querySelector('.bundle-qty-selector');
        const qtyValue = card.querySelector('.bundle-qty-value');

        if (count > 0) {
            card.classList.add('selected');
            if (addBtn) addBtn.style.display = 'none';
            if (qtySelector) qtySelector.style.display = 'flex';
            if (qtyValue) qtyValue.textContent = count;
        } else {
            card.classList.remove('selected');
            if (addBtn) addBtn.style.display = 'flex';
            if (qtySelector) qtySelector.style.display = 'none';
        }
    }

    isBundleComplete() {
        const isComplete = (() => {
            if (this.bundleMode === 'simple') {
                return this.selectedItems.length >= this.itemsRequired;
            } else if (this.bundleMode === 'multi_category') {
                return this.categories.every(category => {
                    const categoryItems = this.selectedItems.filter(item => item.category === category.id);
                    return categoryItems.length >= category.itemsRequired;
                });
            } else if (this.bundleMode === 'tiered') {
                const minItems = this.itemsRequired || 1;
                return this.selectedItems.length >= minItems;
            } else if (this.bundleMode === 'workflow') {
                return this.selectedItems.length >= this.workflowSteps.length;
            }
            return false;
        })();

        console.log('BundleBuilder: isBundleComplete Check:', {
            mode: this.bundleMode,
            count: this.selectedItems.length,
            required: this.itemsRequired,
            isComplete: isComplete
        });

        return isComplete;
    }

    updateSummaryBar() {
        console.log('updateSummaryBar called, selected items:', this.selectedItems.length);

        // Update sticky summary title based on completion status
        this.updateSummaryTitle();

        // Toggle visibility class instead of display property
        // Always show summary bar
        this.summaryBar.classList.remove('bundle-summary-bar--hidden');

        console.log('Showing summary bar');
        this.summaryBar.classList.remove('bundle-summary-bar--hidden');

        // Toggle has-items class for sticky behavior override
        if (this.selectedItems.length > 0) {
            this.summaryBar.classList.add('has-items');
        } else {
            this.summaryBar.classList.remove('has-items');
        }

        // Update title for tiered bundles
        if (this.bundleMode === 'tiered') {
            this.updateTieredTitle();
        }

        const container = this.summaryBar.querySelector('.bundle-summary__items');
        if (!container) return;

        // Clear existing content
        container.innerHTML = '';

        // Render selected items
        this.selectedItems.forEach((item, index) => {
            const itemDiv = document.createElement('div');
            itemDiv.className = 'bundle-summary-item';

            const priceDisplay = item.comparePrice > item.price ?
                `<span style="text-decoration: line-through; opacity: 0.7;">$${item.comparePrice.toFixed(2)}</span>` :
                `$${item.price.toFixed(2)}`;

            const variantDisplay = item.variant ? `<div class="bundle-summary-item__variant">${item.variant}</div>` : '';

            // --- NEW: Upcharge Badge Logic ---
            let upchargeBadge = '';
            if (this.showUpchargeBadge && this.upchargeBaseAmount > 0 && item.price > this.upchargeBaseAmount) {
                const upcharge = item.price - this.upchargeBaseAmount;
                upchargeBadge = `<div class="bundle-summary-item__upcharge-badge">+ $${upcharge.toFixed(0)}</div>`;
            }

            // Rendering preorder badge in summary
            let preorderBadge = '';
            if (item.isPreorder) {
                const leadTime = item.preorderLeadTime || 30;
                const deliveryDate = new Date();
                deliveryDate.setDate(deliveryDate.getDate() + leadTime);
                const dateStr = deliveryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                preorderBadge = `<div class="bundle-summary-item__preorder-badge">Will Ship By: ${dateStr}</div>`;
            }

            itemDiv.innerHTML = `
        <div class="bundle-summary-item__image-wrapper">
            <img src="${item.image}" class="bundle-summary-item__image" alt="${item.title}">
            ${upchargeBadge}
        </div>
        <div class="bundle-summary-item__info">
            <div class="bundle-summary-item__title">${item.title}</div>
            ${variantDisplay}
            <div class="bundle-summary-item__price">${priceDisplay}</div>
            ${preorderBadge}
        </div>
        <button class="bundle-summary-item__remove" data-index="${index}">Remove</button>
    `;

            container.appendChild(itemDiv);
        });

        // Add skeleton loaders for remaining slots
        const remaining = this.getRemainingSlots();
        for (let i = 0; i < remaining; i++) {
            const skeletonDiv = document.createElement('div');
            skeletonDiv.className = 'bundle-summary-item bundle-summary-item--skeleton';

            skeletonDiv.innerHTML = `
          <div class="bundle-summary-item__image-wrapper skeleton"></div>
          <div class="bundle-summary-item__info">
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>
        `;

            container.appendChild(skeletonDiv);
        }

        // Event delegation already handles remove buttons in handleContainerClick
        // No need to bind here - this was causing double removal!

        // Update CTA price
        this.updateCTAPrice();
    }

    getRemainingSlots() {
        if (this.bundleMode === 'simple') {
            return this.itemsRequired - this.selectedItems.length;
        } else if (this.bundleMode === 'multi_category') {
            const totalRequired = this.categories.reduce((sum, cat) => sum + cat.itemsRequired, 0);
            return totalRequired - this.selectedItems.length;
        } else if (this.bundleMode === 'workflow') {
            // Workflow logic?
        }
        return 0;
    }
    async updateCTAPrice() {
        const ctaBtn = document.querySelector('.bundle-summary__cta');
        const btnText = ctaBtn ? ctaBtn.querySelector('.bundle-summary__cta-text') : null;

        const isComplete = this.isBundleComplete();
        let total = 0;
        let compareTotal = 0;
        let upchargeTotal = 0;

        // 1. Calculate upcharges
        if (this.upchargeBaseAmount > 0) {
            this.selectedItems.forEach(item => {
                const surcharge = Math.max(0, (parseFloat(item.price) || 0) - this.upchargeBaseAmount);
                upchargeTotal += surcharge;
            });
        }

        // 2. Base Totals (Individual sums)
        this.selectedItems.forEach((item) => {
            const p = parseFloat(item.price) || 0;
            const cp = parseFloat(item.comparePrice) || 0;
            total += p;
            compareTotal += cp > p ? cp : p;
        });

        // 3. Override for Bundle Pricing if Complete
        if (isComplete) {
            if (this.bundleMode === 'tiered' && this.tiers.length > 0) {
                const count = this.selectedItems.length;
                const matchedTier = [...this.tiers].sort((a, b) => b.items - a.items).find(t => count >= t.items);
                if (matchedTier) {
                    // Try to get price directly from Shopify Variant for perfect accuracy
                    let tierBasePrice = matchedTier.price;
                    if (this.bundleProductData && matchedTier.variantId) {
                        const variant = this.bundleProductData.variants.find(v => String(v.id) === String(matchedTier.variantId));
                        if (variant) {
                            tierBasePrice = variant.price / 100;
                            console.log(`BundleBuilder: Synced Price from Variant ${variant.id}: $${tierBasePrice}`);
                        }
                    }

                    total = tierBasePrice + upchargeTotal;

                    // Sync compare price from variant too if preferred
                    if (matchedTier.comparePrice > 0) {
                        compareTotal = matchedTier.comparePrice + upchargeTotal;
                    }

                    console.log('BundleBuilder: Tiered Price Applied (+Upcharge):', { total, upchargeTotal, tierBasePrice });
                }
            } else if (this.bundlePrice > 0) {
                total = this.bundlePrice + upchargeTotal;
            }

            // 3b. Add Upsell Prices to Total (Only if complete/checkout phase)
            const upsellBtns = document.querySelectorAll('.upsell-item-add-btn.added');
            upsellBtns.forEach(btn => {
                const upsellPrice = parseFloat(btn.dataset.price) || 0;
                const upsellCompare = parseFloat(btn.dataset.comparePrice) || upsellPrice;
                total += upsellPrice;
                compareTotal += upsellCompare;
                console.log(`BundleBuilder: Adding Upsell Price: $${upsellPrice}`);
            });
        }

        // 4. Update DOM
        const savingsRow = document.querySelector('.bundle-summary__savings');
        if (savingsRow) {
            const regPriceEl = savingsRow.querySelector('.bundle-summary__regular-price');
            const totalEl = savingsRow.querySelector('.bundle-summary__total-savings'); // This is now 'Bundle Price:' span

            if (regPriceEl) regPriceEl.textContent = `$${compareTotal.toFixed(2)}`;
            if (totalEl) totalEl.textContent = `$${total.toFixed(2)}`;

            // Show savings row only when bundle is complete
            savingsRow.style.display = isComplete ? 'flex' : 'none';
        }

        if (btnText) {
            let targetText = isComplete ? (this.buttonSuccess || 'Checkout Now') : (this.buttonPending || 'Add [count] more');
            btnText.textContent = this.replacePlaceholders(targetText);
            ctaBtn.disabled = !isComplete;
        }

        // Upsell visibility toggle - Only show if complete AND has items in track
        const upsellSection = document.querySelector('.bundle-summary-upsell');
        const upsellTrack = upsellSection ? upsellSection.querySelector('.upsell-slider-track') : null;
        const hasUpsellContent = upsellTrack ? !!upsellTrack.querySelector('.upsell-item') : false;
        const debugNotice = upsellSection ? upsellSection.querySelector('.upsell-debug-notice') : null;

        // Permanently hide the debug notice in JS if it exists
        if (debugNotice) debugNotice.style.display = 'none';

        if (upsellSection) {
            if (isComplete && hasUpsellContent) {
                console.log('BundleBuilder: Showing upsell section (isComplete and hasContent)');
                upsellSection.style.setProperty('display', 'block', 'important');
            } else {
                console.log('BundleBuilder: Hiding upsell section (incomplete or no items)');
                upsellSection.style.setProperty('display', 'none', 'important');
            }
        }
        else {
            console.warn('BundleBuilder: Upsell section not found in DOM');
        }
    }

    handleUpsellAdd(btn) {
        console.log('BundleBuilder: handleUpsellAdd triggered');
        const variantId = btn.dataset.variantId;

        if (btn.classList.contains('added')) {
            // Toggle Off
            btn.classList.remove('added');
            btn.textContent = this.container.dataset.upsellBtnText || 'Add';
            this.upsellVariantIds.delete(variantId);
        } else {
            // Toggle On
            btn.classList.add('added');
            btn.textContent = 'ADDED';
            this.upsellVariantIds.add(variantId);
        }
        console.log('BundleBuilder: Upsell Variants Selected:', Array.from(this.upsellVariantIds));

        // Trigger price update immediately
        this.updateCTAPrice();
    }

    async fetchDynamicUpsells() {
        console.log('BundleBuilder: fetchDynamicUpsells method entered.');
        if (this.isFetchingUpsells) {
            console.warn('BundleBuilder: fetchDynamicUpsells already in progress, returning.');
            return;
        }

        const isComplete = this.isBundleComplete();
        const upsellSection = document.querySelector('.bundle-summary-upsell');

        if (!isComplete) {
            console.log('BundleBuilder: Bundle not complete, hiding and skipping dynamic upsells.');
            if (upsellSection) upsellSection.style.setProperty('display', 'none', 'important');
            return;
        }

        if (!this.summaryBar) {
            console.error('BundleBuilder: this.summaryBar is UNDEFINED in fetchDynamicUpsells!');
            return;
        }

        const track = this.summaryBar.querySelector('.upsell-slider-track');
        const slider = this.summaryBar.querySelector('.upsell-slider');
        const header = this.summaryBar.querySelector('.upsell-header');
        const debugNotice = this.summaryBar.querySelector('.upsell-debug-notice');

        console.log('BundleBuilder: DOM Elements found:', {
            track: !!track,
            slider: !!slider,
            header: !!header,
            debugNotice: !!debugNotice
        });

        if (!track) {
            console.error('BundleBuilder: .upsell-slider-track not found in DOM inside summaryBar!', this.summaryBar);
            return;
        }

        // If bundle is empty, restore original static upsells
        if (this.selectedItems.length === 0) {
            console.log('BundleBuilder: Bundle empty, restoring static upsells');
            if (this.currentUpsellSeedId !== null) {
                track.innerHTML = this.originalUpsellHTML;
                this.currentUpsellSeedId = null;
                this.syncUpsellButtons(track);

                // Toggle visibility based on whether original HTML has items
                const hasStaticItems = track.querySelector('.upsell-item');
                if (slider) slider.style.display = hasStaticItems ? 'block' : 'none';
                if (header) header.style.display = hasStaticItems ? 'flex' : 'none';
                if (debugNotice) debugNotice.style.display = hasStaticItems ? 'none' : 'block';
            }
            return;
        }

        // Try to get a seed ID from the selection. 
        let seedItem = this.selectedItems[this.selectedItems.length - 1];
        let seedId = seedItem ? seedItem.productId : null;

        console.log('BundleBuilder: fetchDynamicUpsells data check:', {
            selectedItemsCount: this.selectedItems.length,
            seedItem: !!seedItem,
            seedId: seedId,
            currentUpsellSeedId: this.currentUpsellSeedId
        });

        if (!seedId) {
            console.error('BundleBuilder: productId is MISSING or NULL from the selected item!', seedItem);
            // Fallback: try to find ANY productId in the selection
            const fallbackItem = this.selectedItems.find(it => it.productId);
            if (fallbackItem) {
                seedId = fallbackItem.productId;
                console.log('BundleBuilder: Using fallback productId from selection:', seedId);
            } else {
                console.error('BundleBuilder: No productId found in entire selection!');
                return;
            }
        }

        if (seedId === this.currentUpsellSeedId) {
            console.log('BundleBuilder: Recommendations already showing for seed:', seedId);
            return;
        }

        this.isFetchingUpsells = true;
        this.currentUpsellSeedId = seedId;
        track.classList.add('fetching');
        if (debugNotice) debugNotice.style.display = 'none';

        const baseUrl = this.container.dataset.recommendationsUrl || '/recommendations/products';
        const sectionId = 'bundle-upsell-recommendations';
        const url = `${baseUrl}?product_id=${seedId}&limit=10&section_id=${sectionId}`;

        try {
            console.log(`BundleBuilder: Fetching dynamic upsells for product ${seedId} at ${url}...`);
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

            const htmlText = await response.text();
            console.log('BundleBuilder: Received response length:', htmlText.length);

            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, 'text/html');
            const newContent = doc.querySelector('.upsell-recommendations-container');

            if (newContent && newContent.querySelector('.upsell-item')) {
                console.log('BundleBuilder: Found recommendations in response. Updating UI.');
                track.innerHTML = newContent.innerHTML;
                this.syncUpsellButtons(track);

                // Make visible
                if (upsellSection) upsellSection.style.setProperty('display', 'block', 'important');
                if (slider) slider.style.display = 'block';
                if (header) header.style.display = 'flex';
                if (debugNotice) debugNotice.style.display = 'none';
            } else {
                console.warn('BundleBuilder: No recommendations found for seed', seedId, '. Falling back to original.');
                track.innerHTML = this.originalUpsellHTML;
                this.currentUpsellSeedId = null; // Reset so we can try again on next add
                this.syncUpsellButtons(track);

                // Reset visibility based on original state
                const hasStaticItems = track.querySelector('.upsell-item');
                if (slider) slider.style.display = hasStaticItems ? 'block' : 'none';
                if (header) header.style.display = hasStaticItems ? 'flex' : 'none';
                if (debugNotice) debugNotice.style.display = hasStaticItems ? 'none' : 'block';

                // If NOTHING to show, hide the whole container to avoid showing the red box to customers
                if (!hasStaticItems && upsellSection) {
                    console.log('BundleBuilder: No static OR dynamic upsells. Hiding section.');
                    upsellSection.style.setProperty('display', 'none', 'important');
                }
            }
        } catch (err) {
            console.error('BundleBuilder: Error fetching recommendations:', err);
            // On error, restore original to not leave it empty
            track.innerHTML = this.originalUpsellHTML;
            this.currentUpsellSeedId = null;
            this.syncUpsellButtons(track);

            const hasStaticItems = track.querySelector('.upsell-item');
            if (slider) slider.style.display = hasStaticItems ? 'block' : 'none';
            if (header) header.style.display = hasStaticItems ? 'flex' : 'none';
            if (debugNotice) debugNotice.style.display = hasStaticItems ? 'none' : 'block';

            if (!hasStaticItems && upsellSection) {
                upsellSection.style.setProperty('display', 'none', 'important');
            }
        } finally {
            this.isFetchingUpsells = false;
            track.classList.remove('fetching');
        }
    }

    syncUpsellButtons(container) {
        // Ensure "ADDED" state is preserved for currently active upsells
        const buttons = container.querySelectorAll('.upsell-item-add-btn');
        buttons.forEach(btn => {
            if (this.upsellVariantIds.has(btn.dataset.variantId)) {
                btn.classList.add('added');
                btn.textContent = 'ADDED';
            }
        });
    }

    handleUpsellVariantChange(select) {
        const variantId = select.value;
        const option = select.options[select.selectedIndex];
        const price = option.dataset.price;
        const comparePrice = option.dataset.comparePrice;
        const item = select.closest('.upsell-item');
        const btn = item.querySelector('.upsell-item-add-btn');

        // If it was already added, we need to update the Set with the new ID
        if (btn.classList.contains('added')) {
            this.upsellVariantIds.delete(btn.dataset.variantId);
            this.upsellVariantIds.add(variantId);
        }

        // Update button dataset
        btn.dataset.variantId = variantId;
        btn.dataset.price = price;
        btn.dataset.comparePrice = comparePrice;

        // Update visual price in the card
        const priceEl = item.querySelector('.upsell-item-price');
        const compareEl = item.querySelector('.upsell-item-compare');

        if (priceEl && option.dataset.moneyPrice) {
            priceEl.textContent = option.dataset.moneyPrice;
        }

        if (compareEl && option.dataset.moneyCompare) {
            compareEl.textContent = option.dataset.moneyCompare;
            const hasCompare = parseFloat(option.dataset.comparePrice) > parseFloat(option.dataset.price);
            compareEl.style.display = hasCompare ? 'inline' : 'none';
        }

        // Recalculate total if added
        if (btn.classList.contains('added')) {
            this.updateCTAPrice();
        }
    }


    initVariantModal() {
        this.variantModal = document.getElementById('variant-slide-up-modal');
        if (!this.variantModal) {
            // Try to find it by class if ID missing?
            this.variantModal = document.querySelector('.variant-slide-up-modal');
        }

        if (!this.variantModal) {
            console.error('BundleBuilder: Variant Modal HTML Element NOT FOUND!');
            return;
        }

        this.modalContent = this.variantModal.querySelector('.variant-slide-up-body');

        // Close Handlers
        const closeBtn = this.variantModal.querySelector('.variant-slide-up-close');
        const backdrop = this.variantModal.querySelector('.variant-slide-up-backdrop');

        const closeHandler = () => {
            this.variantModal.classList.remove('active');
            document.body.style.overflow = '';

            // Show Summary Bar
            const summaryBar = document.querySelector('.bundle-summary-bar');
            if (summaryBar) summaryBar.classList.remove('hidden-behind-modal');
        };

        if (closeBtn) closeBtn.addEventListener('click', closeHandler);
        if (backdrop) backdrop.addEventListener('click', closeHandler);

        // CRITICAL FIX FOR SAFARI: Use visualViewport to set exact height
        // Safari's backdrop-filter doesn't work with vh units during address bar transitions
        this.updateModalHeight = () => {
            if (!this.variantModal || !window.visualViewport) return;
            const height = window.visualViewport.height;
            this.variantModal.style.height = `${height}px`;
            console.log('Safari Fix: Set modal height to', height, 'px');
        };

        // Update on viewport changes (Safari address bar expand/collapse)
        if (window.visualViewport) {
            console.log('Safari Fix: Setting up visualViewport listeners');
            window.visualViewport.addEventListener('resize', this.updateModalHeight);
            window.visualViewport.addEventListener('scroll', this.updateModalHeight);
            // Set initial height
            this.updateModalHeight();
        } else {
            console.warn('Safari Fix: visualViewport API not supported');
        }

        console.log('BundleBuilder: initVariantModal success');
    }

    renderModalOptions(card) {
        this.modalContent.innerHTML = '';

        const optionSections = Array.from(card.querySelectorAll('.product-options__section'));
        if (optionSections.length === 0) {
            console.warn('BundleBuilder: No .product-options__section found in card');
        }

        const renderedOptionKeys = new Set();

        optionSections.forEach(section => {
            // Robust Key: Use name OR position to uniquely identify the option set
            const optName = (section.dataset.optionName || '').trim().toLowerCase();
            const optPos = section.dataset.optionIndex || section.dataset.position || '';
            const uniqueKey = `${optName}-${optPos}`;

            // If we've already rendered buttons for this Size/Color, skip duplicates
            if (renderedOptionKeys.has(uniqueKey)) return;

            let hasRenderedThisSection = false;
            const grid = document.createElement('div');
            grid.className = 'variant-modal-options-grid';

            // 1. Try Pills
            const pills = section.querySelectorAll('.variant-pill');
            if (pills.length > 0) {
                pills.forEach(pill => {
                    const btn = this.createModalOptionBtn(
                        pill.textContent.trim(),
                        pill.dataset.value,
                        pill.classList.contains('selected'),
                        pill.disabled || pill.classList.contains('disabled'),
                        () => {
                            pill.click();
                            this.handleModalSelectionSuccess();
                        }
                    );
                    grid.appendChild(btn);
                    hasRenderedThisSection = true;
                });
            }

            // 2. Try Select (if no pills or if section IS the select)
            if (!hasRenderedThisSection && section.tagName === 'SELECT') {
                Array.from(section.options).forEach(opt => {
                    // Skip placeholders (usually value is empty or includes "Choose")
                    if (!opt.value || opt.disabled) return;

                    const btn = this.createModalOptionBtn(
                        opt.textContent.trim(),
                        opt.value,
                        opt.selected,
                        opt.disabled,
                        () => {
                            section.value = opt.value;
                            section.dispatchEvent(new Event('change', { bubbles: true }));
                            this.handleModalSelectionSuccess();
                        }
                    );
                    grid.appendChild(btn);
                    hasRenderedThisSection = true;
                });
            }

            if (hasRenderedThisSection) {
                renderedOptionKeys.add(uniqueKey);
                this.modalContent.appendChild(grid);
            }
        });

        // Special Case: Color Swatches loop outside standard wrapper?
        // In our liquid, colors are: .product-options__value--circle
        const colorSwatches = card.querySelectorAll('.product-options__value--circle');
        if (colorSwatches.length > 0 && optionSections.length === 0) {
            const grid = document.createElement('div');
            grid.className = 'variant-modal-options-grid';
            grid.style.gridTemplateColumns = 'repeat(5, 1fr)'; // More dense for colors

            colorSwatches.forEach(swatch => {
                const input = swatch.querySelector('input');
                const value = input ? input.value : swatch.dataset.value;
                const isActive = swatch.classList.contains('active');

                const btn = this.createModalOptionBtn(
                    value,
                    value,
                    isActive,
                    false, // Assuming colors avail
                    () => {
                        this.handleSwatchClick(swatch); // Reuse existing handler
                        this.handleModalSelectionSuccess();
                    }
                );
                // Override style for color?
                // For now keeping text, but user can customize.
                grid.appendChild(btn);
            });
            this.modalContent.appendChild(grid);
        }
    }

    createModalOptionBtn(label, value, isSelected, isDisabled, onClick) {
        const btn = document.createElement('button');
        btn.className = `variant-modal-option-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`;
        btn.textContent = label;
        btn.type = 'button';

        if (!isDisabled) {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                // Find all siblings and remove selected
                const siblings = btn.parentElement.querySelectorAll('.variant-modal-option-btn');
                siblings.forEach(s => s.classList.remove('selected'));
                btn.classList.add('selected');
                onClick();
            });
        }
        return btn;
    }

    handleModalSelectionSuccess() {
        // Trigger Add to Cart on the hidden card
        // We use a short timeout to allow any React/Event loop updates to process state
        setTimeout(() => {
            const addBtn = this.activeModalCard.querySelector('.bundle-add-to-bundle-btn');
            if (addBtn) {
                // Check if button is enabled (valid variant)
                if (!addBtn.disabled) {
                    this.handleAddClick(addBtn);

                    // Close Modal
                    this.variantModal.classList.remove('active');
                    document.body.style.overflow = '';

                    // Show Summary Bar
                    const summaryBar = document.querySelector('.bundle-summary-bar');
                    if (summaryBar) summaryBar.classList.remove('hidden-behind-modal');

                } else {
                    // Feedback? "Variant unavailable"
                    console.warn('Selected variant is unavailable or invalid');
                }
            }
        }, 50);
    }


    updateSummaryTitle() {
        const titleElement = this.summaryBar.querySelector('.bundle-summary__title');
        if (!titleElement) return;

        const isComplete = this.isBundleComplete();
        let targetText = isComplete ?
            (this.titleSuccess || this.defaultSummaryTitle) :
            (this.titlePending || this.defaultSummaryTitle);

        // Update the text with placeholders replaced
        titleElement.innerHTML = this.replacePlaceholders(targetText);
        console.log('BundleBuilder: Updated summary title to:', titleElement.innerHTML, 'Complete:', isComplete);

        if (isComplete) {
            this.enableCheckout();
        } else {
            this.disableCheckout();
        }
    }

    startReservedTimer() {
        if (!this.timerEnabled || this.timerInterval) return;

        console.log('BundleBuilder: Starting reserved timer...');
        const timerContainer = this.summaryBar.querySelector('.bundle-reserved-timer');
        if (!timerContainer) {
            console.error('BundleBuilder: Timer container .bundle-reserved-timer not found!');
            return;
        }

        this.timerEndTime = Date.now() + (this.timerMinutes * 60 * 1000);
        timerContainer.style.display = 'flex';

        this.updateTimerDisplay();
        this.timerInterval = setInterval(() => this.updateTimerDisplay(), 1000);
    }

    updateTimerDisplay() {
        const timerContainer = this.summaryBar.querySelector('.bundle-reserved-timer');
        const timerDisplay = timerContainer?.querySelector('.timer-display');
        if (!timerDisplay) return;

        const remaining = Math.max(0, this.timerEndTime - Date.now());
        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);

        timerDisplay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        if (remaining <= 0) {
            this.stopReservedTimer();
        }
    }

    stopReservedTimer() {
        if (this.timerInterval) {
            console.log('BundleBuilder: Stopping reserved timer.');
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }

        const timerContainer = this.summaryBar.querySelector('.bundle-reserved-timer');
        if (timerContainer) {
            timerContainer.style.display = 'none';
        }
    }

    // Removed getCurrentTier method

    updateProgress() {
        const progressBar = this.summaryBar.querySelector('.bundle-summary__progress-fill');

        if (!progressBar) return;

        let currentCount = this.selectedItems.length;
        let totalRequired = 0;

        // Calculate goal based on mode
        if (this.bundleMode === 'simple') {
            totalRequired = this.itemsRequired;
        } else if (this.bundleMode === 'tiered') {
            totalRequired = this.itemsRequired || 3;
        } else if (this.bundleMode === 'workflow') {
            totalRequired = this.workflowSteps.length;
        }

        // Update Progress Bar
        const percent = Math.min(100, Math.round((currentCount / totalRequired) * 100));
        progressBar.style.width = `${percent}%`;
    }

    replacePlaceholders(text) {
        if (!text) return '';

        let goal = this.itemsRequired || 3;
        if (this.bundleMode === 'workflow') {
            goal = this.workflowSteps.length;
        } else if (this.bundleMode === 'tiered') {
            goal = this.itemsRequired || 3;
            // Get highest tier goal if applicable
            if (this.tiers && this.tiers.length > 0) {
                goal = this.tiers[this.tiers.length - 1].items;
            }
        }

        const remaining = Math.max(0, goal - this.selectedItems.length);
        const current = this.selectedItems.length;

        return text
            .replace(/\[count\]/g, remaining)
            .replace(/\[items_needed\]/g, remaining)
            .replace(/\[quantity\]/g, remaining)
            .replace(/\[total\]/g, goal)
            .replace(/\[goal\]/g, goal)
            .replace(/\[current\]/g, current)
            .replace(/\[product_type\]/g, this.productType);
    }

    updateCategoryTabs() {
        if (this.bundleMode !== 'multi_category') return;

        const tabs = document.querySelectorAll('.bundle-category-tab');
        tabs.forEach(tab => {
            const categoryId = tab.dataset.category;
            const category = this.categories.find(c => c.id === categoryId);
            if (!category) return;

            const categoryItems = this.selectedItems.filter(item => item.category === categoryId);
            const count = tab.querySelector('.category-count');
            if (count) {
                count.textContent = `(${categoryItems.length} / ${category.itemsRequired})`;
            }
        });
    }
    // Removed updateTierProgress method

    updateWorkflowButtons() {
        if (this.bundleMode !== 'workflow') return;

        const steps = document.querySelectorAll('.bundle-workflow-step');
        steps.forEach((step, index) => {
            const stepId = step.dataset.stepId;
            const stepItems = this.selectedItems.filter(item => item.step === stepId);
            const nextBtn = step.querySelector('.workflow-next-btn');

            if (nextBtn) {
                nextBtn.disabled = stepItems.length === 0;
            }
        });
    }

    toggleSummary() {
        const header = this.summaryBar.querySelector('.bundle-summary__header');
        const content = this.summaryBar.querySelector('.bundle-summary__content');
        if (!header || !content) return;

        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        const nextState = !isExpanded;

        header.setAttribute('aria-expanded', nextState);

        if (nextState) {
            // Expanding: Remove hidden attribute first so slideToggle can calculate height
            content.removeAttribute('hidden');
            if (typeof content.slideDown === 'function') {
                content.slideDown(400);
            } else {
                content.style.display = 'block';
            }
        } else {
            // Collapsing
            if (typeof content.slideUp === 'function') {
                content.slideUp(400);
                setTimeout(() => content.setAttribute('hidden', ''), 400); // Re-add hidden after animation
            } else {
                content.style.display = 'none';
                content.setAttribute('hidden', '');
            }
        }
    }

    enableCheckout() {
        const btn = document.querySelector('.bundle-summary__cta');
        if (btn) btn.disabled = false;

        // Start reserved timer when bundle becomes complete
        this.startReservedTimer();
    }

    disableCheckout() {
        const btn = document.querySelector('.bundle-summary__cta');
        if (btn) btn.disabled = true;
    }

    async handleCheckout() {
        const success = await this.addToCart();
        if (success) {
            this.clearProgress();
            // Redirect straight to checkout
            window.location.href = '/checkout';
        }
    }

    saveProgress() {
        if (!this.productId) return;
        try {
            const key = `bundle_progress_${this.productId}`;
            // Clean items of circular references or large DOM nodes before saving
            const itemsToSave = this.selectedItems.map(item => ({
                id: item.id,
                variant: item.variant,
                variantId: item.variantId,
                title: item.title,
                price: item.price,
                comparePrice: item.comparePrice,
                image: item.image,
                quantity: item.quantity
            }));
            localStorage.setItem(key, JSON.stringify(itemsToSave));
            console.log('BundleBuilder: Progress saved to localStorage');
        } catch (e) {
            console.error('BundleBuilder: Error saving progress:', e);
        }
    }

    loadProgress() {
        if (!this.productId) return;
        const key = `bundle_progress_${this.productId}`;
        const saved = localStorage.getItem(key);

        if (saved) {
            try {
                this.selectedItems = JSON.parse(saved);
                console.log('BundleBuilder: Loaded progress from localStorage:', this.selectedItems);
            } catch (e) {
                console.error('BundleBuilder: Error parsing saved progress:', e);
                this.selectedItems = [];
            }
        }
    }

    clearProgress() {
        if (!this.productId) return;
        const key = `bundle_progress_${this.productId}`;
        localStorage.removeItem(key);
        console.log('BundleBuilder: Progress cleared from localStorage');
    }

    initColorFilter() {
        // Stub to prevent errors if referenced
    }

    initCategoryTabs() {
        // Stub to prevent errors if referenced
    }

    initWorkflow() {
        // Stub to prevent errors if referenced
    }

    updateTierProgress() {
        // Stub to prevent errors if referenced
    }

    loadTiersFromDOM() {
        // Read from the tier cards which contain the authoritative data
        const cards = document.querySelectorAll('.tier-card');
        if (cards.length > 0) {
            return Array.from(cards).map(card => {
                const rawPrice = card.dataset.tierPrice || card.querySelector('.tier-label')?.textContent || '0';
                return {
                    items: parseInt(card.querySelector('.tier-count')?.textContent) || 0,
                    price: parseFloat(rawPrice.replace(/[^0-9.]/g, '')) || 0,
                    variantId: card.querySelector('.tier-variant-id')?.textContent.trim() || ''
                };
            });
        }

        // Fallback
        const segments = document.querySelectorAll('.tier-segment');
        if (segments.length > 0) {
            return Array.from(segments).map(s => {
                const rawPrice = s.dataset.price || '0';
                return {
                    items: parseInt(s.dataset.items) || 0,
                    price: parseFloat(rawPrice.replace(/[^0-9.]/g, '')) || 0,
                    variantId: (s.dataset.variantId || '').toString().trim()
                };
            });
        }

        return [];
    }

    async addToCart() {
        const btn = document.querySelector('.bundle-summary__cta');
        if (btn) {
            btn.textContent = 'Adding...';
            btn.disabled = true;
        }

        // Theme-Specific: Start Loader
        document.body.dispatchEvent(new Event('startLoading'));

        const bundleId = Date.now();
        const logicMode = this.container.dataset.logicMode || 'dummy'; // Default to dummy as requested
        let itemsToAdd = [];

        // =======================================================
        // OPTION A: DUMMY PRODUCT MODE (Fixed Price via Variant)
        // =======================================================
        if (logicMode === 'native_bundle') {
            console.log('BundleBuilder: Calculating Native Bundle Variant...');

            // 1. Get Product Data
            const jsonScript = document.getElementById('bundle-product-data');
            if (!jsonScript) {
                console.error('BundleBuilder: Missing #bundle-product-data script');
                alert('Configuration Error: Product Data Missing');
                if (btn) btn.disabled = false;
                return;
            }
            const productData = JSON.parse(jsonScript.textContent);

            // 2. Construct Target Options from Selection
            // Map to object with both title and variant for flexible matching
            const selectedItemsData = this.selectedItems.map(item => ({
                title: item.title,
                variant: item.variant
            }));
            const selectedOptionsLog = selectedItemsData.map(i => `${i.title} (or ${i.variant})`);
            console.log('BundleBuilder: Selected Options:', selectedOptionsLog);

            // 3. Find Matching Variant (Robust)
            const normalize = str => (str || '').toLowerCase().trim();

            // A. Try Strict Order Match
            let matchedVariant = productData.variants.find(variant => {
                // Tighten: Ensure variant has EXACTLY the same number of options as selected items
                if (variant.options.length !== selectedItemsData.length) return false;

                return selectedItemsData.every((item, index) => {
                    const vOpt = normalize(variant.options[index]);
                    const itemTitle = normalize(item.title);
                    const itemVariant = normalize(item.variant);

                    // Match if variant option is title, or variant title, or if title contains variant option
                    return vOpt === itemTitle ||
                        (itemVariant && vOpt === itemVariant) ||
                        (itemTitle && itemTitle.includes(vOpt)) ||
                        (itemVariant && itemVariant.includes(vOpt));
                });
            });

            // B. Try Permutation Match (Ignore Order)
            if (!matchedVariant) {
                console.log('BundleBuilder: Strict match failed. Trying permutation match...');
                matchedVariant = productData.variants.find(variant => {
                    // Tighten: Ensure variant has EXACTLY the same number of options as selected items
                    if (variant.options.length !== selectedItemsData.length) return false;

                    const vOptions = [...variant.options].map(normalize).sort();
                    // We need to find a permutation where EACH item matches ONE unique option
                    // This is complex because "Gold" might match multiple items.
                    // Simplified Permutation: Check if the set of V-Options matches the set of Item-Variants (or titles)

                    // Strategy: Clone lists and find pairs
                    let remainingVOptions = [...vOptions];
                    return selectedItemsData.every(item => {
                        const itemTitle = normalize(item.title);
                        const itemVariant = normalize(item.variant);

                        const matchIndex = remainingVOptions.findIndex(vOpt =>
                            vOpt === itemTitle ||
                            (itemVariant && vOpt === itemVariant) ||
                            (itemTitle && itemTitle.includes(vOpt)) ||
                            (itemVariant && itemVariant.includes(vOpt))
                        );
                        if (matchIndex > -1) {
                            remainingVOptions.splice(matchIndex, 1);
                            return true;
                        }
                        return false;
                    });
                });
            }

            // C. Debug Mode (if still failed)
            if (!matchedVariant) {
                console.warn('BundleBuilder: NO MATCH FOUND.');
                console.log('User Selected:', selectedOptionsLog);
                console.log('Available Variants (First 5):', productData.variants.slice(0, 5).map(v => v.options));
                alert(`Could not find a bundle combination for: \n${selectedOptionsLog.join(' + ')}\n\nPlease check that your Bundle Product in the App has this specific combination created.`);
                if (btn) btn.disabled = false;
                return;
            }

            console.log('BundleBuilder: Found Variant:', matchedVariant.id, matchedVariant.title);

            // 4. Create Payload
            // Use Matched Variant ID

            // Build Properties for "Fake Component" Display
            const properties = {
                '_bundle_id': bundleId,
                '_bundle_mode': 'native', // Marker for backend if needed
                '_bundle_logic': 'dummy_product', // Triggers minicart.liquid to render components
                '_component_count': this.selectedItems.length
            };

            this.selectedItems.forEach((item, index) => {
                const i = index + 1;
                // Prefix with _ to hide from standard Shopify property list (redundant with minicart components)
                properties[`_Included Item ${i}`] = `${item.title} - ${item.variant || 'Default'}`;
                properties[`_component_title_${i}`] = item.title;
                properties[`_component_variant_${i}`] = item.variant || '';
                // Ensure valid image URL. If missing, use a fallback or handle in Liquid.
                // Assuming item.image is a URL string.
                properties[`_component_image_${i}`] = item.image || '';
                properties[`_component_url_${i}`] = '#';

                // Price logic: Native bundles usually have their own price. 
                // We display individual prices just for show if needed, or 0.
                properties[`_component_price_${i}`] = item.price;
            });

            itemsToAdd = [{
                id: matchedVariant.id,
                quantity: 1,
                properties: properties
            }];
        }
        // =======================================================
        // OPTION A: DUMMY PRODUCT MODE (Fixed Price via Variant)
        // =======================================================
        else if (logicMode === 'dummy') {
            let targetVariantId = this.container.dataset.dummyId;
            const uBase = this.upchargeBaseAmount || 0;

            // 1. Calculate base bundle price based on mode
            let baseBundlePrice = this.bundlePrice || 0;
            const count = this.selectedItems.length;

            if (this.tiers.length > 0) {
                // Find all tiers for this item count and use the LOWEST one as the base for upcharge logic
                const countTiers = this.tiers.filter(t => parseInt(t.items) === count);
                if (countTiers.length > 0) {
                    const minTier = countTiers.reduce((prev, curr) => prev.price < curr.price ? prev : curr);
                    baseBundlePrice = minTier.price;
                }
            }

            // 2. Calculate upcharges (Exact, no rounding)
            let totalUpcharge = 0;

            if (uBase > 0) {
                this.selectedItems.forEach(item => {
                    const surcharge = Math.max(0, item.price - uBase);
                    totalUpcharge += surcharge;
                });
            }

            // 3. Target Price (In Cents for integer-safe comparison)
            const targetPrice = baseBundlePrice + totalUpcharge;
            const targetCents = Math.round(targetPrice * 100);

            console.log('BundleBuilder: Cents Matching:', {
                targetPrice: targetPrice,
                targetCents: targetCents,
                base: baseBundlePrice,
                totalUpcharge: totalUpcharge
            });

            // 4. Find the variant that matches this EXACT price in cents
            if (this.tiers.length > 0) {
                console.log('BundleBuilder: Checking Tiers for Match...', this.tiers);
                // Exact Cent match
                let matchedTier = this.tiers.find(t => {
                    const tierCents = Math.round(parseFloat(t.price) * 100);
                    console.log(`Checking match: target=${targetCents} vs tier=${tierCents} (items: ${t.items})`);
                    return tierCents === targetCents && parseInt(t.items) === count;
                });

                if (!matchedTier) {
                    // One final "loose" check if it's off by exactly 1-2 cents due to float math
                    matchedTier = this.tiers.find(t => {
                        const tierCents = Math.round(parseFloat(t.price) * 100);
                        return Math.abs(tierCents - targetCents) <= 2 && parseInt(t.items) === count;
                    });
                }

                if (matchedTier && matchedTier.variantId) {
                    targetVariantId = matchedTier.variantId;
                    console.log('BundleBuilder: SUCCESS - Matched Variant ID from Tiers:', targetVariantId);
                } else {
                    console.warn('BundleBuilder: NO PRICE MATCH in Tiers. Checking full product data for closest match...');
                    // Fallback: Check full product data for hidden variants (e.g. Upcharges)
                    if (this.bundleProductData && this.bundleProductData.variants) {
                        // [v13] Closest Match Logic (Availability Aware)
                        // Find the variant with the absolute smallest price difference
                        // Break ties with availability
                        let bestMatch = null;
                        let minimumDifference = Infinity;

                        this.bundleProductData.variants.forEach(v => {
                            const diff = Math.abs(v.price - targetCents);

                            // Optimization: If exact match and available, take it immediately
                            if (diff === 0 && v.available) {
                                minimumDifference = 0;
                                bestMatch = v;
                                return;
                            }

                            // Update if:
                            // 1. New diff is strictly smaller
                            // 2. Diff is same, but new variant is available and current is not
                            if (diff < minimumDifference) {
                                minimumDifference = diff;
                                bestMatch = v;
                            } else if (diff === minimumDifference) {
                                if (v.available && bestMatch && !bestMatch.available) {
                                    bestMatch = v;
                                }
                            }
                        });

                        if (bestMatch) {
                            if (!bestMatch.available) {
                                console.warn('BundleBuilder: Closest match found but is OUT OF STOCK. Checkout may fail.', bestMatch);
                            }

                            // Log warning if diff is "large" (e.g. > $1) just for debugging
                            if (minimumDifference > 100) {
                                console.warn('BundleBuilder: Closest variant match is off by > $1.00:', {
                                    target: targetCents,
                                    match: bestMatch.price,
                                    diff: minimumDifference
                                });
                            }

                            targetVariantId = bestMatch.id;
                            console.log(`BundleBuilder: SUCCESS - Found Closest Match (Diff: ${minimumDifference} cents). ID: ${targetVariantId}. Available: ${bestMatch.available}`);
                        }
                    }

                    if (!targetVariantId) {
                        // Final Fallback: Count match
                        console.warn('BundleBuilder: No price match found. Falling back to simple count match (ignoring price).');
                        const countMatch = this.tiers.find(t => parseInt(t.items) === count);
                        if (countMatch) targetVariantId = countMatch.variantId;
                    }
                }
            }

            if (!targetVariantId || targetVariantId === 'undefined') {
                const errorMsg = 'Configuration Error: No matching price found for $' + targetPrice.toFixed(2) + '. Please ensure you have a Price Tier variant created for this amount.';
                console.error('BundleBuilder:', errorMsg);
                alert(errorMsg);
                if (btn) btn.disabled = false;
                return;
            }

            // Create Properties List
            const properties = {
                '_bundle_id': bundleId,
                '_bundle_type': this.bundleMode,
                '_bundle_logic': 'dummy_product',
                '_component_count': this.selectedItems.length
            };

            // Add each selected item as a property
            this.selectedItems.forEach((item, index) => {
                const i = index + 1;
                properties[`Item ${i}`] = `${item.title} - ${item.variant || 'Default'}`;
                properties[`_component_title_${i}`] = item.title;
                properties[`_component_variant_${i}`] = item.variant || '';
                properties[`_component_variant_id_${i}`] = item.id;
                properties[`_component_image_${i}`] = item.image || '';
                properties[`_component_url_${i}`] = '#';
                properties[`_component_price_${i}`] = item.price;
            });

            itemsToAdd = [{
                id: String(targetVariantId).trim(),
                quantity: 1,
                properties: properties
            }];
            console.log('BundleBuilder: Adding Dummy Product to Cart:', JSON.stringify(itemsToAdd, null, 2));
        }
        // =======================================================
        // OPTION B: INDIVIDUAL ITEMS MODE (Variable Price + Discount Code)
        // =======================================================
        else {
            // ... (Original Logic with Discount Code) ...

            // --- DISCOUNT CODE LOGIC ---
            let discountCode = '';
            if (this.bundleMode === 'simple' || this.bundleMode === 'workflow' || this.bundleMode === 'multi_category') {
                discountCode = this.container.dataset.discountCode || '';
            } else if (this.bundleMode === 'tiered' && this.tiers.length > 0) {
                const count = this.selectedItems.length;
                const matchedTier = this.tiers.find(t => t.items === count);
                if (matchedTier && matchedTier.code) { // Note: mapped to .code in old loadTiers, careful if we changed it
                    // Actually we changed loadTiers to .variantId, but let's check if we want to support both or if schema switch broke this.
                    // User said "yes update everything" context of "Dummy Product". 
                    // So I will assume Individual mode is Deprecated for tiers or uses the variantId field as code? No that's messy.
                    // I will strip the discount code logic here to keep it clean, or just leave it broken/unused.
                    // Let's rely on Simple mode discount code if anything.
                }
                discountCode = this.container.dataset.discountCode || ''; // Fallback
            }

            if (discountCode) {
                fetch(`/discount/${discountCode}`).catch(e => console.error(e));
                localStorage.setItem('storedDiscount', discountCode);
            }

            // Calculate Price (Visual Only)
            const totalPrice = this.selectedItems.reduce((sum, item) => sum + item.price, 0);
            let finalPrice = totalPrice;
            if (this.isBundleComplete()) {
                if (this.bundleMode === 'simple' && this.bundlePrice > 0) finalPrice = this.bundlePrice;
                else if (this.bundleMode === 'tiered') finalPrice = totalPrice; // Tiered usually implies discount code
            }

            // CONVERT TO CENTS
            const finalPriceCents = Math.round(finalPrice * 100);
            const itemsCount = this.selectedItems.length;
            const basePrice = Math.floor(finalPriceCents / itemsCount);
            const remainder = finalPriceCents % itemsCount;
            const priceDisplay = Number.isInteger(finalPrice) ? finalPrice : finalPrice.toFixed(2);
            const badgeText = `$${priceDisplay} for ${itemsCount} items`;

            itemsToAdd = this.selectedItems.map((item, index) => {
                let itemPrice = basePrice;
                if (index === itemsCount - 1) itemPrice += remainder;
                return {
                    id: item.id,
                    quantity: 1,
                    properties: {
                        '_bundle_id': bundleId,
                        '_bundle_type': this.bundleMode,
                        '_bundle_item': `${index + 1} of ${itemsCount}`,
                        '_bundle_price': finalPriceCents,
                        '_bundle_item_price': itemPrice,
                        '_bundle_badge_text': badgeText,
                        'Variant': item.variant || 'Default'
                    }
                };
            });
        }

        // Inject upsell items if selected
        if (this.upsellVariantIds && this.upsellVariantIds.size > 0) {
            if (!itemsToAdd) itemsToAdd = [];

            this.upsellVariantIds.forEach(id => {
                itemsToAdd.push({
                    id: id,
                    quantity: 1,
                    properties: {
                        '_bundle_upsell': 'true'
                    }
                });
                console.log('BundleBuilder: Injected Upsell Item:', id);
            });
        }

        try {
            // Final validation of itemsToAdd
            if (!itemsToAdd || itemsToAdd.length === 0) {
                console.error('BundleBuilder: itemsToAdd is empty', itemsToAdd);
                throw new Error('No items identified to add to cart. Please check your selection.');
            }

            // [NEW] Availability Check: Find the target variant in local data to check stock
            if (this.bundleProductData && this.bundleProductData.variants) {
                console.log('BundleBuilder: Checking availability for', itemsToAdd.length, 'items');
                itemsToAdd.forEach(item => {
                    const variant = this.bundleProductData.variants.find(v => String(v.id) === String(item.id));
                    if (variant) {
                        console.log(`BundleBuilder: Variant Availability Info for ${item.id}:`, {
                            title: variant.title,
                            available: variant.available,
                            inventory_quantity: variant.inventory_quantity,
                            inventory_policy: variant.inventory_policy
                        });
                        if (!variant.available) {
                            const msg = `CRITICAL: The Bundle Variant (ID: ${item.id}, Title: ${variant.title}) is marked as OUT OF STOCK in Shopify. You must add stock or allow overselling for this variant in the Shopify Admin.`;
                            console.error(msg);
                            alert(msg);
                        }
                    } else {
                        const msg = `CRITICAL: Variant ID ${item.id} NOT FOUND in this product's variants. You likely put a PRODUCT ID where a VARIANT ID belongs in the settings. \n\nPlease go to Product -> Variants and copy the specific ID for the correct pack variant.`;
                        console.warn(msg);
                        alert(msg);
                    }
                });
            } else {
                console.error('BundleBuilder: bundleProductData or variants missing!', this.bundleProductData);
            }

            // Exhaustive Logging for Debugging
            console.log('BundleBuilder: Pre-formatting itemsToAdd:', JSON.parse(JSON.stringify(itemsToAdd)));

            // Ensure all IDs are NUMERIC and properties are safe
            itemsToAdd.forEach((item, index) => {
                const rawId = item.id;
                const cleanId = BundleBuilder.cleanId(rawId);

                console.log(`Item ${index} ID check: raw=${rawId}, clean=${cleanId}`);

                if (!cleanId || cleanId === 'undefined' || cleanId === 'null' || cleanId === '') {
                    throw new Error(`Invalid Variant ID identified: "${rawId}". Please check your product configuration.`);
                }

                // IMPORTANT: Use number for Shopify AJAX API to be safe
                item.id = parseInt(cleanId);

                // Convert all properties to strings to avoid serialization issues
                if (item.properties) {
                    Object.keys(item.properties).forEach(key => {
                        if (item.properties[key] === null || item.properties[key] === undefined) {
                            item.properties[key] = '';
                        } else {
                            item.properties[key] = String(item.properties[key]);
                        }
                    });
                }
            });

            const finalPayload = {
                items: itemsToAdd,
                sections: 'ajax-minicart'
            };

            console.log('BundleBuilder: FINAL FETCH PAYLOAD:', JSON.stringify(finalPayload, null, 2));

            let response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(finalPayload)
            });

            // AGGRESSIVE FALLBACK: If status is 422, try a minimal payload
            if (response.status === 422) {
                console.warn('BundleBuilder: 422 Error Detected. Attempting AGGRESSIVE FALLBACK (Minimal Payload)...');
                const minimalItems = itemsToAdd.map(item => ({ id: item.id, quantity: 1 }));
                console.log('BundleBuilder: Minimal Payload:', JSON.stringify(minimalItems));

                const fallbackResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    body: JSON.stringify({ items: minimalItems })
                });

                if (fallbackResponse.ok) {
                    console.log('BundleBuilder: Minimal Fallback SUCCESS! Redirecting...');
                    return true;
                } else {
                    const fbError = await fallbackResponse.json();
                    console.error('BundleBuilder: Minimal Fallback ALSO FAILED:', fbError);
                    // Continue to original error handling with the first response
                }
            }

            if (response.ok) {
                const responseData = await response.json();
                console.log('BundleBuilder: Success Response:', responseData);

                // Theme-Specific Logic: Dispatch event to refresh mini-cart
                document.body.dispatchEvent(new CustomEvent('ajax:addToCart', {
                    detail: { response: responseData }
                }));

                // Open the minicart drawer
                const minicart = document.querySelector('.minicart .push_side');
                if (minicart && !minicart.classList.contains('active')) {
                    minicart.click();
                }

                if (btn) {
                    btn.textContent = 'Added!';
                    setTimeout(() => {
                        btn.textContent = 'Add to cart';
                        btn.disabled = true;
                        if (this.isBundleComplete()) {
                            btn.disabled = false;
                            this.updateCTAPrice();
                        }
                    }, 2000);
                }
                return true; // SUCCESS
            } else {
                const errorData = await response.json();
                console.error('BundleBuilder: Shopify Cart Error Response:', errorData);

                // If the error mentions sections, try one more time without sections
                if (JSON.stringify(errorData).toLowerCase().includes('section') && finalPayload.sections) {
                    console.warn('BundleBuilder: Retrying without sections attribute...');
                    delete finalPayload.sections;
                    const retryResponse = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(finalPayload)
                    });
                    if (retryResponse.ok) return true;
                }

                const errorStr = errorData.description || errorData.message || JSON.stringify(errorData);
                throw new Error(errorStr);
            }
        } catch (error) {
            console.error('Error adding bundle to cart (Catch Block):', error);
            // Reverted to cleaner message for production
            alert(`Checkout Error: ${error.message}`);
            if (btn) {
                btn.textContent = 'Error - Try Again';
                btn.disabled = false;
            }
            return false; // FAILURE
        } finally {
            // Theme-Specific: End Loader
            document.body.dispatchEvent(new Event('endLoading'));
        }
    }

    updateTierProgress() {
        console.log('UpdateTierProgress: Check', this.bundleMode);
        if (this.bundleMode !== 'tiered') return;

        const wrappers = document.querySelectorAll('.bundle-tier-progress-wrapper');
        if (wrappers.length === 0) {
            console.warn('UpdateTierProgress: No wrappers found!');
            return;
        }

        const count = this.selectedItems.length;

        // Process each wrapper independently
        wrappers.forEach(wrapper => {
            wrapper.style.display = 'block';

            // Get location from first segment
            const firstSegment = wrapper.querySelector('.tier-segment');
            const location = firstSegment ? firstSegment.dataset.location : 'default';

            // Get location-specific elements
            const segments = wrapper.querySelectorAll(`.tier-segment-${location}`);
            const labels = wrapper.querySelectorAll(`.tier-label-${location}`);
            const badge = wrapper.querySelector(`.tier-badge-${location}`);

            console.log(`UpdateTierProgress [${location}]: Count=${count}, Segments=${segments.length}`);

            // Store original text on first run (per location)
            const textsKey = `tierOriginalTexts_${location}`;
            if (!this[textsKey]) {
                this[textsKey] = [];
                segments.forEach(segment => {
                    const span = segment.querySelector('span');
                    this[textsKey].push(span ? span.textContent : '');
                });
            }

            let activeTierIndex = -1;

            segments.forEach((segment, index) => {
                const tierItems = parseInt(segment.dataset.items);
                const prevTierItems = index > 0 ? parseInt(segments[index - 1].dataset.items) : 0;
                const label = labels[index];
                const span = segment.querySelector('span');
                const originalText = this[textsKey][index] || '';

                // Reset classes
                segment.classList.remove('completed', 'active', 'locked');
                segment.style.background = ''; // Clear inline gradient

                // LOGIC:
                // 1. Completed: We have enough items for this tier.
                if (count >= tierItems) {
                    segment.classList.add('completed');

                    // Checkmark Logic: Replace text with Checkmark Icon
                    if (span) {
                        span.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
                        span.style.filter = 'none';
                    }

                    // Hide label underneath to reduce redundancy
                    if (label) label.textContent = '';
                }
                // 2. Active: We have passed the previous tier (or are at 0) and are working towards this one.
                else if (count >= prevTierItems) {
                    segment.classList.add('active');
                    activeTierIndex = index; // Track which tier is active

                    // Restore original text
                    if (span) span.textContent = originalText;

                    // Calculate gradient percentage
                    const range = tierItems - prevTierItems;
                    const progress = count - prevTierItems;
                    const percent = Math.min((progress / range) * 100, 100);

                    // Set CSS custom property for smooth animation
                    // This can be transitioned smoothly by CSS
                    segment.style.setProperty('--tier-fill-width', `${percent}%`);

                    // Update Text: "Add X more"
                    const needed = tierItems - count;
                    if (label) label.textContent = `ADD ${needed} MORE`;
                    if (span) span.style.filter = 'none';
                }
                // 3. Locked: Not yet reached previous tier
                else {
                    segment.classList.add('locked');

                    // Restore original text
                    if (span) span.textContent = originalText;

                    const needed = tierItems - count;
                    if (label) label.textContent = `ADD ${needed} MORE`;

                    // Blur text
                    if (span) {
                        span.style.filter = 'blur(4px)';
                    }
                }
            });

            // Update Badge based on active tier
            if (badge) {
                if (activeTierIndex === -1 || activeTierIndex === 0) {
                    // No badge for tier 0 (baseline)
                    badge.style.display = 'none';
                } else {
                    badge.style.display = 'block';

                    // Position badge above active segment using simple percentage
                    // Calculate center position of the active tier segment
                    const totalSegments = segments.length;
                    const segmentWidth = 100 / totalSegments; // Each segment takes equal width
                    const centerPercent = (activeTierIndex * segmentWidth) + (segmentWidth / 2);
                    badge.style.left = centerPercent + '%';

                    // Set badge text: "BEST VALUE" for last tier, "BETTER VALUE" for others
                    if (activeTierIndex === segments.length - 1) {
                        badge.textContent = 'BEST VALUE';
                    } else {
                        badge.textContent = 'BETTER VALUE';
                    }
                }
            }
        });
    }

    updateTieredTitle() {
        const titleElement = this.summaryBar.querySelector('.bundle-summary__title');
        if (!titleElement) return;

        // Respect Liquid override
        if (titleElement.dataset.override === 'true') return;

        const count = this.selectedItems.length;

        // Find which tier we're currently in
        if (this.tiers && this.tiers.length > 0) {
            let currentTier = this.tiers[0]; // Default to first tier

            // Find the active or last completed tier
            for (let i = 0; i < this.tiers.length; i++) {
                if (count >= this.tiers[i].items) {
                    currentTier = this.tiers[i];
                } else if (count < this.tiers[i].items) {
                    // We're working towards this tier
                    currentTier = this.tiers[i];
                    break;
                }
            }

            // Update title with current tier info
            titleElement.textContent = `${currentTier.items} Items, Starting at $${currentTier.price}`;
        }
    }

    loadCategories() {
        const grids = document.querySelectorAll('.bundle-category-grid');
        return Array.from(grids).map(grid => ({
            id: grid.dataset.category,
            itemsRequired: parseInt(grid.dataset.itemsRequired) || 1
        }));
    }

    loadWorkflowSteps() {
        const steps = document.querySelectorAll('.bundle-workflow-step');
        return Array.from(steps).map(step => ({
            id: step.dataset.stepId,
            number: parseInt(step.dataset.step)
        }));
    }

    initStickyReveal() {
        // Sticky bar is always active in this mode
        if (this.summaryBar) {
            this.summaryBar.classList.add('visible');
        }
    }

    addItemFromQuickView(itemData, btn) {
        console.log('BundleBuilder: addItemFromQuickView triggered', itemData);

        // Update button state immediately
        if (btn) {
            const originalText = btn.innerHTML;
            btn.innerHTML = '<span class="text">✓ ADDED</span>';
            btn.classList.add('added');
            // Force full width if not already
            btn.style.width = '100%';
        }

        // Find the card in the grid to sync state
        const card = document.querySelector(`.bundle-product-item[data-product-id="${itemData.productId}"]`);

        // Build full item data
        const item = {
            id: itemData.variantId,
            title: itemData.title,
            image: itemData.image,
            price: (parseFloat(itemData.price) || 0) / 100,
            comparePrice: (parseFloat(itemData.comparePrice) || 0) / 100,
            variant: itemData.variantTitle || 'Default',
            cardElement: card || null
        };

        if (this.bundleMode === 'multi_category') {
            item.category = itemData.category;
            item.categoryName = itemData.categoryName;
        } else if (this.bundleMode === 'workflow') {
            item.step = itemData.step;
            item.stepName = itemData.stepName;
            item.stepNumber = parseInt(itemData.stepNumber);
        }

        // Add the item (BundleBuilder logic handles state/summary)
        this.addItem(item, card);

        // Close all dialog modals (Quick View) with delay
        setTimeout(() => {
            document.querySelectorAll('dialog-modal').forEach(modal => {
                const dialog = modal.querySelector('dialog');
                if (dialog && dialog.open) {
                    dialog.classList.remove('transition');
                    dialog.classList.add('closing-down'); // Trigger slide-down animation

                    // Wait for animation to finish before closing
                    setTimeout(() => {
                        dialog.close();
                        dialog.classList.remove('closing-down');
                        document.documentElement.classList.remove('dialog-open'); // Ensure scroll lock is removed
                    }, 400);
                }
            });

            // Optional: Reset button text after closing
            if (btn) {
                setTimeout(() => {
                    btn.innerHTML = originalText;
                    btn.classList.remove('added');
                }, 500);
            }
        }, 600);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.bundleBuilder = new BundleBuilder(); });
} else {
    window.bundleBuilder = new BundleBuilder();
}

// Shopify Theme Editor re-initialization
document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.bundle-builder')) {
        console.log('BundleBuilder: Theme Editor change detected, re-initializing...');
        window.bundleBuilder = new BundleBuilder();
    }
});

// Size Guide Logic
document.addEventListener('DOMContentLoaded', function () {
    const sizeGuideModal = document.getElementById('bundle-size-guide-modal');
    const sizeGuideImg = sizeGuideModal ? sizeGuideModal.querySelector('.bundle-size-guide-img') : null;
    const closeButtons = document.querySelectorAll('[data-close-modal]');

    // Open Modal
    document.addEventListener('click', function (e) {
        const btn = e.target.closest('.bundle-size-guide-btn');
        if (btn && sizeGuideModal && sizeGuideImg) {
            e.preventDefault();
            const imgSrc = btn.dataset.image;
            if (imgSrc) {
                sizeGuideImg.src = imgSrc;
                sizeGuideModal.classList.add('is-active');
                document.body.style.overflow = 'hidden';
            }
        }
    });

    // Close Modal
    closeButtons.forEach(btn => {
        btn.addEventListener('click', function () {
            if (sizeGuideModal) {
                sizeGuideModal.classList.remove('is-active');
                document.body.style.overflow = '';
            }
        });
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && sizeGuideModal && sizeGuideModal.classList.contains('is-active')) {
            sizeGuideModal.classList.remove('is-active');
            document.body.style.overflow = '';
        }
    });
});
